import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: { id: string; name: string };
  onDeleted?: () => void;
};

type RpcError = {
  data?: { code?: string; activeCount?: number };
  message?: string;
};

export function DeleteTemplateDialog({ open, onOpenChange, template, onDeleted }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeError, setActiveError] = useState<{ activeCount: number } | null>(null);

  // Reset error state when dialog re-opens.
  useEffect(() => {
    if (!open) setActiveError(null);
  }, [open]);

  const remove = useMutation({
    mutationFn: (input: { id: string }) => rpcClient.onboardingTemplates.remove(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: orpc.onboardingTemplates.list.queryOptions().queryKey,
      });
      toast({ title: "Template excluído" });
      onOpenChange(false);
      onDeleted?.();
    },
    onError: (err: unknown) => {
      const rpcErr = err as RpcError;
      if (rpcErr?.data?.code === "active_onboardings") {
        setActiveError({ activeCount: rpcErr.data.activeCount ?? 0 });
        return;
      }
      toast({
        title: "Não foi possível excluir o template",
        description: rpcErr?.message ?? String(err),
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="dialog-delete-template">
        {activeError ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Não é possível excluir '{template.name}'</AlertDialogTitle>
              <AlertDialogDescription>
                {activeError.activeCount === 1
                  ? "1 onboarding ativo está usando este template."
                  : `${activeError.activeCount} onboardings ativos estão usando este template.`}{" "}
                Cancele-os primeiro ou aguarde a graduação.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => onOpenChange(false)}>OK</AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir '{template.name}'?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Recursos no escopo do template são removidos; a
                Company Library não é afetada.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={remove.isPending}>Cancelar</AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate({ id: template.id })}
                data-testid="button-confirm-delete-template"
              >
                {remove.isPending ? "Excluindo…" : "Excluir"}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
